// Jednoduchý sloupcový graf (SVG, bez cizí knihovny – CSP plochy): skupiny sloupců po obdobích (dny, měsíce),
// více řad s barvou a legendou, osa Y s 4 dílky, popisky osy X řídce (aby se nepřekrývaly), hodnota v bublině (title).
import { h } from '../dom';

export interface ChartSeries { name: string; color: string; values: number[] }

const NS = 'http://www.w3.org/2000/svg';
const el = (tag: string, attrs: Record<string, string | number>, text?: string): SVGElement => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  if (text !== undefined) e.textContent = text;
  return e;
};

/** „Hezký“ dílek osy (1, 2, 5 × 10^n) pro 4 dílky – celá čísla, maximum = 4 × dílek. */
function niceStep(v: number): number {
  const raw = Math.max(v, 1) / 4;
  const p = 10 ** Math.floor(Math.log10(raw));
  for (const m of [1, 2, 5, 10]) if (m * p >= raw) return Math.max(1, m * p);
  return 10 * p;
}

/** width = šířka v pixelech (obvykle šířka prvku, kam se graf vloží); popisky mají skutečnou velikost, graf se neroztahuje. */
export function barChart(labels: string[], series: ChartSeries[], format: (v: number) => string, height = 240, width = 0): HTMLElement {
  const W = Math.max(320, width || labels.length * Math.max(14, series.length * 8 + 6) + 60, labels.length * (series.length * 3 + 2) + 60);
  const H = height;
  const pad = { l: 52, r: 8, t: 10, b: 28 };
  const max = niceStep(Math.max(0, ...series.flatMap((s) => s.values))) * 4;
  const svg = el('svg', { viewBox: `0 0 ${W} ${H}`, width: W, height: H, class: 'chart-svg', role: 'img' }) as SVGSVGElement;
  const plotW = W - pad.l - pad.r;
  const plotH = H - pad.t - pad.b;
  for (let i = 0; i <= 4; i++) { // mřížka a osa Y
    const y = pad.t + plotH - (plotH * i) / 4;
    svg.append(el('line', { x1: pad.l, x2: W - pad.r, y1: y, y2: y, class: i ? 'chart-grid' : 'chart-axis' }));
    svg.append(el('text', { x: pad.l - 6, y: y + 4, 'text-anchor': 'end', class: 'chart-label' }, format((max * i) / 4)));
  }
  const groupW = plotW / Math.max(1, labels.length);
  const barW = Math.max(2, Math.min(18, (groupW - 4) / Math.max(1, series.length)));
  const every = Math.ceil(labels.length / Math.max(1, Math.floor(plotW / 60))); // popisek nejvýš po ~60 px
  labels.forEach((lab, i) => {
    const gx = pad.l + i * groupW + (groupW - barW * series.length) / 2;
    series.forEach((s, j) => {
      const v = s.values[i] ?? 0;
      const bh = (plotH * v) / max;
      const r = el('rect', { x: gx + j * barW, y: pad.t + plotH - bh, width: Math.max(1, barW - 1), height: Math.max(0, bh), rx: 1.5, fill: s.color, class: 'chart-bar' });
      r.append(el('title', {}, `${lab} – ${s.name}: ${format(v)}`));
      svg.append(r);
    });
    if (i % every === 0) svg.append(el('text', { x: pad.l + i * groupW + groupW / 2, y: H - 8, 'text-anchor': 'middle', class: 'chart-label' }, lab));
  });
  const legend = h('div', { class: 'chart-legend' }, series.map((s) => h('span', { class: 'chart-legend-item' }, h('span', { class: 'chart-dot', style: { background: s.color } }), s.name)));
  return h('div', { class: 'chart' }, legend, h('div', { class: 'chart-scroll' }, svg as unknown as HTMLElement));
}
